/**
 * CrossfadeHero — seamless looping background video.
 *
 * Ports the two-<video> crossfade IIFE from the original AB8 storefront pages:
 * two stacked muted videos play the same clip; as one nears its end the other
 * fades in, hiding the hard loop seam. Falls back gracefully if the browser
 * blocks autoplay.
 *
 *   <CrossfadeHero src="/videos/cream-loop.mp4" overlay="rgba(0,0,0,.25)">
 *     ...hero content...
 *   </CrossfadeHero>
 */
import { useEffect, useRef } from 'react';

const FADE_SECONDS = 1;

export default function CrossfadeHero({ src, overlay = 'transparent', className = '', style = {}, children }) {
  const aRef = useRef(null);
  const bRef = useRef(null);

  useEffect(() => {
    const a = aRef.current;
    const b = bRef.current;
    if (!a || !b) return;

    let active = a;
    let standby = b;
    standby.style.opacity = '0';
    active.style.opacity = '1';

    [a, b].forEach(v => { v.muted = true; v.play?.().catch(() => {}); });
    // Stagger the standby clip so a fresh frame is ready when we cross over.
    b.currentTime = 0;

    const onTime = () => {
      if (!active.duration) return;
      const remaining = active.duration - active.currentTime;
      if (remaining <= FADE_SECONDS && standby.paused === false) return;
      if (remaining <= FADE_SECONDS) {
        standby.currentTime = 0;
        standby.play?.().catch(() => {});
        standby.style.opacity = '1';
        active.style.opacity = '0';
        const tmp = active; active = standby; standby = tmp;
      }
    };

    const id = setInterval(onTime, 120);
    return () => clearInterval(id);
  }, [src]);

  const videoStyle = {
    position: 'absolute', inset: 0, width: '100%', height: '100%',
    objectFit: 'cover', transition: `opacity ${FADE_SECONDS}s ease-in-out`,
  };

  return (
    <div className={className} style={{ position: 'relative', overflow: 'hidden', ...style }}>
      <video ref={aRef} style={videoStyle} src={src} muted loop={false} playsInline autoPlay preload="auto" />
      <video ref={bRef} style={videoStyle} src={src} muted loop={false} playsInline preload="auto" />
      <div style={{ position: 'absolute', inset: 0, background: overlay }} />
      <div style={{ position: 'relative', zIndex: 1, height: '100%' }}>{children}</div>
    </div>
  );
}
